import { queryOptions } from '@tanstack/react-query';

import { fetchNpiSuggestionsGateway } from './fetchNpiSuggestionsGateway';
import type { NpiSuggestionsRequest } from '../model/types';
import type { NpiSuggestionsGateway } from '../ports/npiSuggestionsGateway';

import { TEN_MINUTES } from '@/constants/time';
import { userScoped } from '@/util/userScope';

/**
 * Starting a run for the person signing up, per name and state they asked
 * for. Starting twice is safe: the server attaches to the run in flight or
 * returns the finished one. Scoped to the viewer rather than the user because
 * a pending Google signup asks before it has an account. Not retried: a
 * failure here is the network, and the screen moves on to the name field.
 */
export const getNpiSuggestionsQueryOptions = userScoped(
  'viewer',
  (
    userId: string | undefined,
    request: NpiSuggestionsRequest,
    gateway: NpiSuggestionsGateway = fetchNpiSuggestionsGateway
  ) =>
    queryOptions({
      queryKey: ['npiSuggestions', userId, request.name ?? null, request.state ?? null],
      queryFn: () => gateway.startSuggestions(request),
      staleTime: TEN_MINUTES,
      retry: false,
      refetchOnWindowFocus: false,
    })
);

/**
 * One run read back from the server. The hook polls it while the realtime
 * channel is not live, and re-reads it on a completion hint.
 */
export const getNpiSuggestionRunQueryOptions = userScoped(
  'viewer',
  (
    userId: string | undefined,
    runId: string,
    gateway: NpiSuggestionsGateway = fetchNpiSuggestionsGateway
  ) =>
    queryOptions({
      queryKey: ['npiSuggestionRun', userId, runId],
      queryFn: () => gateway.readRun(runId),
      retry: 2,
      refetchOnWindowFocus: false,
    })
);
